import axios from 'axios'

/**
 * Passkey（WebAuthn）の画面側。
 *
 * ここがやるのは3つだけ。
 *
 *   1. サーバから受け取った文字列を、ブラウザが要る形（ArrayBuffer）へ直す
 *   2. ブラウザに鍵を作らせる／署名させる
 *   3. 返ってきたものを文字列へ直してサーバへ送る
 *
 * **判断はしない。** どのドメインの鍵か（RP ID）も、challenge も、誰の鍵かも、
 * 全部サーバが決める。画面が決められる形にすると、偽サイトが自分の都合の
 * よい値を入れられるので、Passkey の売りであるフィッシング耐性が消える。
 */

const BASE = process.env.VUE_APP_API_ORIGIN

/**
 * もう一度ログインの確認が要る、という応答か。
 *
 * サーバは 403 と code: 'REAUTHENTICATION_REQUIRED' を返す。401にしないのは、
 * 401だと画面がログアウトとして扱い、作業の途中で全部やり直しになるため。
 * 呼び出し側は、その操作の前でだけ本人確認をやり直せばよい。
 */
export function isReauthenticationRequired (error) {
  const response = error && error.response
  if (!response || response.status !== 403) return false
  return Boolean(response.data) &&
    response.data.code === 'REAUTHENTICATION_REQUIRED'
}

/**
 * 通信そのものが届かなかったか。
 *
 * 応答が1つも無いときだけ true。サーバが断ったのか、そもそも届いて
 * いないのかを分けるために使う。**混ぜると「もう一度」で直るものと、
 * 何度やっても直らないものが同じ文言になる。**
 *
 * <b>判定はここ1か所に置く。</b>同じ式を各サービスへ写すと、条件が
 * 増えたときに片方だけ直る。以前は authSessions / externalIdpLogin /
 * mfa に同じ3行が別々に置かれていた。
 */
export function isNetworkFailure (error) {
  return Boolean(error) && !error.response
}

/**
 * PREIKAがパスキーのログインを開けているか、サーバに聞く。
 *
 * 画面側の設定だけで決めると、画面は開いているのにサーバは閉じている
 * （押せるのに通らない）、あるいはその逆、というずれが起きる。
 *
 * **聞けなかったら閉じている扱い。** 分からないときにボタンを出すと、
 * 押しても必ず失敗する入口を見せることになる。
 */
export async function isPasskeyLoginAvailable () {
  try {
    const response = await axios.get(BASE + '/passkey/availability')
    return Boolean(response.data && response.data.passkeyLoginAvailable)
  } catch (error) {
    return false
  }
}

/**
 * <b>いまログインしている人が、自分のパスキーを登録できるか。</b>
 *
 * {@link isPasskeyLoginAvailable} とは別物。あちらは<b>まだ誰か分からない</b>
 * ログイン画面向けで、契約企業ごとの設定を含まない（含めると、メール
 * アドレスを入れなくても会社の設定を外から確かめられる）。
 *
 * 返るのは2つ。
 *
 *   offered          … PREIKA がパスキーのログインを開けているか
 *   allowedByCompany … その会社が利用者に許しているか
 *
 * <b>分けて返る理由</b>があって、案内の向き先が変わる。開けていないのは
 * こちら（PREIKA）側の話で、会社の管理者に言っても始まらない。
 *
 * <b>聞けなかったら閉じている扱い。</b>分からないときに勧めると、押しても
 * 必ず断られる入口を見せることになる。2026-09-19 に実際にそうなった。
 */
export async function fetchPasskeyRegistrable () {
  try {
    const response = await axios.get(BASE + '/passkey/registrable')
    const data = response.data || {}
    return {
      offered: Boolean(data.offered),
      allowedByCompany: Boolean(data.allowedByCompany)
    }
  } catch (error) {
    return { offered: false, allowedByCompany: false }
  }
}

/** ブラウザがPasskeyに対応しているか。 */
export function isPasskeySupported () {
  return typeof window !== 'undefined' &&
    typeof window.PublicKeyCredential === 'function' &&
    Boolean(window.navigator && window.navigator.credentials)
}

/**
 * Base64URL → ArrayBuffer。
 *
 * サーバとのやり取りは全部 Base64URL に揃えてある。`+` `/` `=` を使う
 * 普通のBase64だと、URLやJSONの取り回しで壊れることがある。
 */
function toBuffer (value) {
  const normalized = String(value || '').replace(/-/g, '+').replace(/_/g, '/')
  const padded = normalized + '='.repeat((4 - normalized.length % 4) % 4)
  const binary = window.atob(padded)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

/** ArrayBuffer → Base64URL。 */
function toText (buffer) {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return window.btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

/**
 * Passkeyでログインする。
 *
 * 使える鍵の一覧（allowCredentials）は渡さない。サーバも返してこない。
 * 渡すと「その人がPasskeyを持っているか」が外から分かる。ブラウザが
 * 自分の持っている鍵から選ぶ。
 *
 * @returns サーバのログイン応答（パスワードでログインしたときと同じ形）
 */
export async function signInWithPasskey () {
  const started = await axios.post(BASE + '/passkey/authentication/start')
  const options = started.data

  const credential = await window.navigator.credentials.get({
    publicKey: {
      challenge: toBuffer(options.challenge),
      rpId: options.rpId,
      timeout: options.timeoutMillis,
      userVerification: 'preferred'
    }
  })

  if (!credential) {
    throw new Error('PASSKEY_CANCELLED')
  }

  const finished = await axios.post(BASE + '/passkey/authentication/finish', {
    credentialId: toText(credential.rawId),
    authenticatorData: toText(credential.response.authenticatorData),
    clientDataJson: toText(credential.response.clientDataJSON),
    signature: toText(credential.response.signature),
    userHandle: credential.response.userHandle
      ? toText(credential.response.userHandle)
      : null
  })
  return finished.data
}

/**
 * この端末にPasskeyを1本登録する。
 *
 * ログイン済みの本人だけが呼べる。サーバ側でもセッションを見ている
 * （開始時と完了時の両方）。
 *
 * @param label 「Windows PC」など、本人が付ける名前
 */
/**
 * サーバが出した条件で、この端末に鍵を1本作る。
 *
 * ログイン済みの登録と、招待からの登録で**同じ手順を使う**。
 * 分けて書くと、片方だけ residentKey の指定が古いまま残る——
 * 「登録できたのにログインで見つからない鍵」ができる。
 *
 * @param options サーバが返した条件
 * @returns ブラウザが作った鍵（サーバへ送る形に整えたもの）
 */
async function createCredential (options) {
  const credential = await window.navigator.credentials.create({
    publicKey: {
      challenge: toBuffer(options.challenge),
      rp: { id: options.rpId, name: options.rpName },
      user: {
        id: toBuffer(options.userHandle),
        name: options.userName || '',
        displayName: options.userDisplayName || options.userName || ''
      },
      // ES256 と RS256。いまの端末はどちらかで作る。
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 },
        { type: 'public-key', alg: -257 }
      ],
      // 既に登録済みの鍵では作らせない。同じ端末で二重に作ると、
      // 一覧に同じものが並んで、どれを消してよいか分からなくなる。
      excludeCredentials: (options.excludeCredentialIds || []).map(id => ({
        type: 'public-key',
        id: toBuffer(id)
      })),
      // ログインではメールアドレスも鍵の一覧も渡さない（完全な
      // discoverable 方式）。それに合わせて、登録側でも端末に鍵を
      // **覚えさせる**ことを必須にする。
      //
      // preferred のままだと、端末によっては「登録は成功したのに、
      // ログインのときに見つからない鍵」ができる。利用者からは
      // 「登録したのに使えない」に見える。
      //
      // requireResidentKey は古い実装向けの同じ意味の指定。両方書く。
      authenticatorSelection: {
        residentKey: 'required',
        requireResidentKey: true,
        userVerification: 'preferred'
      },
      timeout: options.timeoutMillis,
      // 端末の出自の証明は求めない。求めても弾く基準が無く、利用者には
      // 「よく分からない確認」が1つ増えるだけになる。
      //
      // **求めないので、証明は保存されない。** あとで「特定のセキュリティ
      // キーだけ許可」のような運用にするなら、既に登録済みの鍵は登録し直しに
      // なる可能性がある。
      attestation: 'none'
    }
  })

  if (!credential) {
    throw new Error('PASSKEY_CANCELLED')
  }

  return {
    credentialId: toText(credential.rawId),
    attestationObject: toText(credential.response.attestationObject),
    clientDataJson: toText(credential.response.clientDataJSON),
    transports: typeof credential.response.getTransports === 'function'
      ? credential.response.getTransports().join(',')
      : null
  }
}

/**
 * この端末にPasskeyを1本登録する。
 *
 * ログイン済みの本人だけが呼べる。サーバ側でもセッションを見ている
 * （開始時と完了時の両方）。
 *
 * @param label 「Windows PC」など、本人が付ける名前
 */
export async function registerPasskey (label) {
  const started = await axios.post(BASE + '/passkey/registration/start')
  const credential = await createCredential(started.data)

  await axios.post(BASE + '/passkey/registration/finish', {
    ...credential,
    label: label || null
  })
}

/**
 * <b>招待リンクを持っている人が、最初の鍵を登録する。</b>
 *
 * まだログインしていないので、送るのは招待の token だけ。**誰の鍵として
 * 登録するかはサーバが招待の行から決める。**
 *
 * ログイン済みの登録と違い、既に登録済みの鍵を除外しない。前回の受諾が
 * 鍵を書いたあとで落ちていると鍵が1本残っていることがあり、除外すると
 * <b>同じ端末でやり直せなくなる</b>から（サーバ側も空で返す）。
 *
 * @param token 招待リンクの token
 * @param label 「Windows PC」など、本人が付ける名前
 * @returns 受け取りの結果（message を含む）
 */
export async function acceptInvitationWithPasskey (token, label) {
  const started = await axios.post(
    BASE + '/account-invitations/passkey/start', { token })
  const credential = await createCredential(started.data)

  const accepted = await axios.post(
    BASE + '/account-invitations/passkey/accept', {
      token,
      credential: { ...credential, label: label || null }
    })
  return accepted.data
}

/** 登録済みの鍵の一覧。本人のぶんだけ返る。 */
export async function listPasskeys () {
  const response = await axios.get(BASE + '/passkey/credentials')

  /*
   * **形が違うのは「0本」ではない。**
   *
   * 以前はここで空配列へすり替えていた。読めていないのに「1本も
   * 持っていない」と見えるので、それを判定に使う側が間違える。
   * 実際、登録の案内を「まだ持っていない人だけ」に出す作りにしたとき、
   * 読めなかった人にも出る状態になっていた（2026-09-20 の監査 D1）。
   *
   * 投げておけば、呼ぶ側はそれぞれ正しく振る舞う。
   * 案内する側は出さない（分からないときに勧めない）。
   * 設定画面は「読み込めませんでした」と出す。
   */
  if (!Array.isArray(response.data)) {
    throw new Error('PASSKEY_LIST_UNREADABLE')
  }
  return response.data
}

/** 鍵を1本消す。本人のぶんしか消えない（サーバのSQLで絞っている）。 */
export async function deletePasskey (credentialRowId) {
  await axios.delete(BASE + '/passkey/credentials/' + Number(credentialRowId))
}
